import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormInputText } from '@/shared/components/composites/FormInputText';
import { PortalModal } from '@/shared/components/composites/PortalModal';
import { FormCheckButton } from '@/shared/components/parts/FormCheckButton';
import { zodValidations } from '@/shared/helpers/zod/zodValidations';
import { useTapiFetcher } from '@/shared/hooks/useTapiFetcher';
import type { UsePointHistoryUsage } from '../index.hook';
import type { UserSecurityCodeCheckResponse } from 'u-next/api-myaccount';

type FormValues = {
  securityCode: string;
};

const formSchema = z.object({
  securityCode: zodValidations.securityCodeAndPinCode,
});

export const AdultGenreCheck: React.FC<{
  canBeShownAdultContent: UsePointHistoryUsage['state']['canBeShownAdultContent'];
  showAdultContent: UsePointHistoryUsage['showAdultContent'];
  maskAdultContent: UsePointHistoryUsage['maskAdultContent'];
}> = ({ canBeShownAdultContent, showAdultContent, maskAdultContent }) => {
  const [isVisibleModal, setIsVisibleModal] = React.useState<boolean>(false);
  const { fetcher } = useTapiFetcher<UserSecurityCodeCheckResponse>();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: { securityCode: '' },
    resolver: zodResolver(formSchema),
  });

  const handleChangeCheckBox = () => {
    // haven't show adult genre. check securityCode
    if (!canBeShownAdultContent) {
      reset();
      setIsVisibleModal(true);
      return;
    }

    // have shown adult genre already. update flag
    maskAdultContent();
    setIsVisibleModal(false);
  };

  const onSubmit = async (values: FormValues) => {
    const apiData = await fetcher('/1/user/security_code/check', {
      securityCode: values.securityCode,
    });

    if (!apiData.data.check_flg) {
      setError('securityCode', {
        message: 'セキュリティコードが間違っています。',
      });
      return;
    }

    reset();
    showAdultContent();
    setIsVisibleModal(false);
  };

  return (
    <div>
      <label
        htmlFor="_adultCheck"
        className="grid grid-cols-[auto_1fr] items-center gap-2"
      >
        <FormCheckButton
          id="_adultCheck"
          checked={canBeShownAdultContent}
          onChange={handleChangeCheckBox}
        />
        <div className="text-base2">その他♥作品名を表示する</div>
      </label>
      {isVisibleModal && (
        <PortalModal
          title="セキュリティコード確認"
          content={
            <FormInputText
              {...register('securityCode')}
              type="password"
              label="セキュリティコード"
              maxLength={4}
              inputMode="numeric"
              validationMessage={errors.securityCode?.message}
            />
          }
          primaryButton={{
            type: 'submit',
            text: '確認',
            onClick: () => {
              void handleSubmit(onSubmit)();
            },
          }}
          secondaryButton={{
            type: 'cancel',
            text: 'キャンセル',
            onClick: () => {
              setIsVisibleModal(false);
            },
          }}
        />
      )}
    </div>
  );
};
